import {
  TcsChallanDetailSchema,
  TcsChallanSchema,
  TcsDocumentPresignResponseSchema,
  TcsDocumentRowSchema,
  type CreateTcsChallanRequest,
  type CreateTcsDocumentRequest,
  type TcsChallan,
  type TcsChallanDetail,
  type TcsDocumentKind,
  type TcsDocumentPresignRequest,
  type TcsDocumentPresignResponse,
  type TcsDocumentRow,
  type UpdateTcsChallanRequest,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/** API client for the combined TCS challan endpoints (`/tcs-challans`). */

export const MAX_TCS_DOC_BYTES = 25 * 1024 * 1024;

/** Server returns only `{ id }` on create; the list/detail refetch fills the rest. */
const CreatedIdSchema = z.object({ id: z.string().min(1) });
export type CreatedId = z.infer<typeof CreatedIdSchema>;

export const createChallan = async (body: CreateTcsChallanRequest): Promise<CreatedId> => {
  const { data } = await apiClient.post<unknown>('/tcs-challans', body);
  const { id } = CreatedIdSchema.parse(data);
  return { id };
};

export const listChallans = async (params?: {
  paid?: boolean;
  q?: string;
}): Promise<TcsChallan[]> => {
  const query: Record<string, string> = {};
  if (params?.paid !== undefined) query.paid = String(params.paid);
  if (params?.q) query.q = params.q;
  const { data } = await apiClient.get<unknown>('/tcs-challans', { params: query });
  return z.array(TcsChallanSchema).parse(data);
};

export const getChallan = async (id: string): Promise<TcsChallanDetail> => {
  const { data } = await apiClient.get<unknown>(`/tcs-challans/${id}`);
  return TcsChallanDetailSchema.parse(data);
};

export const patchChallan = async (id: string, body: UpdateTcsChallanRequest): Promise<void> => {
  await apiClient.patch(`/tcs-challans/${id}`, body);
};

export const addInvoices = async (id: string, invoiceIds: string[]): Promise<void> => {
  await apiClient.post(`/tcs-challans/${id}/invoices`, { invoiceIds });
};

export const removeInvoice = async (id: string, invoiceId: string): Promise<void> => {
  await apiClient.delete(`/tcs-challans/${id}/invoices/${invoiceId}`);
};

export const deleteChallan = async (id: string): Promise<void> => {
  await apiClient.delete(`/tcs-challans/${id}`);
};

export const listDocs = async (id: string): Promise<TcsDocumentRow[]> => {
  const { data } = await apiClient.get<unknown>(`/tcs-challans/${id}/documents`);
  return z.array(TcsDocumentRowSchema).parse(data);
};

export const presignDoc = async (
  id: string,
  body: TcsDocumentPresignRequest,
): Promise<TcsDocumentPresignResponse> => {
  const { data } = await apiClient.post<unknown>(`/tcs-challans/${id}/documents/presign`, body);
  return TcsDocumentPresignResponseSchema.parse(data);
};

export const createDoc = async (id: string, body: CreateTcsDocumentRequest): Promise<CreatedId> => {
  const { data } = await apiClient.post<unknown>(`/tcs-challans/${id}/documents`, body);
  const { id: docId } = CreatedIdSchema.parse(data);
  return { id: docId };
};

export const deleteDoc = async (id: string, docId: string): Promise<void> => {
  await apiClient.delete(`/tcs-challans/${id}/documents/${docId}`);
};

/**
 * Three-stage document upload: presign -> raw PUT of the file bytes to the
 * presigned S3 URL (native fetch, NOT apiClient: the URL carries its own auth
 * and must not get an Authorization header) -> create the document row. A
 * failed PUT throws before the create call, so no row is ever created for it.
 */
export const uploadDoc = async (
  id: string,
  file: File,
  kind: TcsDocumentKind,
): Promise<CreatedId> => {
  if (file.size > MAX_TCS_DOC_BYTES) throw new Error(`${file.name} is larger than 25 MB`);
  const contentType = file.type || 'application/octet-stream';
  const presigned = await presignDoc(id, { fileName: file.name, contentType, kind });
  const put = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status}): ${put.statusText}`);
  return createDoc(id, {
    key: presigned.key,
    fileName: file.name,
    contentType,
    sizeBytes: file.size,
    kind,
  });
};
